import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { Ionicons } from "@/src/components/ui/ionicons";
import { AccountSwitcher } from "@/src/components/AccountSwitcher";
import { WebBackButton } from "@/src/components/WebBackButton";
import { useAuth } from "@/src/hooks/useAuth";
import { useActiveAccount } from "@/src/hooks/useActiveAccount";
import { getChatErrorMessage, toMillis } from "@/src/lib/chat";
import {
  isIdentityDmUnread,
  listIdentityDmInbox,
  scopeIdentityDmInbox,
  subscribeIdentityDmInbox,
  type IdentityDmInbox,
  type IdentityDmThread,
} from "@/src/lib/directMessaging";
import {
  isIdentityGroupUnread,
  subscribeIdentityGroupInbox,
  type IdentityGroupInboxThread,
} from "@/src/lib/identityGroups";
import { getPrimaryIdentityBadge } from "@/src/lib/identity";
import { contentMaxWidths } from "@/src/theme/layout";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { useIsFocused } from "@react-navigation/native";
import { router } from "expo-router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, FlatList, Platform, Pressable, RefreshControl, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const BG = surface.secondary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;
const ACCENT = brand.primary;

type InboxRow =
  | { kind: "dm"; id: string; thread: IdentityDmThread; peer: any }
  | { kind: "group"; id: string; thread: IdentityGroupInboxThread };

function formatTime(value: any) {
  const ms = toMillis(value);
  if (!ms) return "";
  const date = new Date(ms);
  const now = new Date();
  return date.toDateString() === now.toDateString()
    ? date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

function identityTitle(identity: any) {
  return identity?.displayName?.trim() || (identity?.handle ? `@${String(identity.handle).replace(/^@/, "")}` : "Identité indisponible");
}

function inboxFingerprint(inbox: IdentityDmInbox) {
  return inbox.threads.map((thread) => `${thread.conversationId}:${thread.updatedAt}:${thread.lastReadAt}`).join("|");
}

export default function MessagesInbox() {
  const { user } = useAuth();
  const { activeAccountId, activeAccount, accounts } = useActiveAccount();
  const isFocused = useIsFocused();
  const [dmInbox, setDmInbox] = useState<IdentityDmInbox>({ identityId: "", threads: [], peers: {} });
  const [groupThreads, setGroupThreads] = useState<IdentityGroupInboxThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const dmFingerprintRef = useRef("");
  const dmLoadRunningForRef = useRef<string | null>(null);
  const activeAccountIdRef = useRef<string | null>(activeAccountId);
  activeAccountIdRef.current = activeAccountId;

  const visibleDmInbox = useMemo<IdentityDmInbox>(
    () => scopeIdentityDmInbox(dmInbox, activeAccountId),
    [activeAccountId, dmInbox]
  );

  const loadDm = useCallback(async (silent = false) => {
    const identityId = activeAccountId;
    if (!identityId) {
      setDmInbox({ identityId: "", threads: [], peers: {} });
      dmFingerprintRef.current = "";
      return;
    }
    if (dmLoadRunningForRef.current === identityId) return;
    dmLoadRunningForRef.current = identityId;
    try {
      const next = await listIdentityDmInbox(identityId);
      if (activeAccountIdRef.current !== identityId || next.identityId !== identityId) return;
      const fingerprint = inboxFingerprint(next);
      if (fingerprint !== dmFingerprintRef.current) {
        dmFingerprintRef.current = fingerprint;
        setDmInbox(next);
      }
    } catch (error) {
      if (!silent && activeAccountIdRef.current === identityId) throw error;
    } finally {
      if (dmLoadRunningForRef.current === identityId) dmLoadRunningForRef.current = null;
    }
  }, [activeAccountId]);

  useEffect(() => {
    dmFingerprintRef.current = "";
    setDmInbox({ identityId: activeAccountId || "", threads: [], peers: {} });
  }, [activeAccountId]);

  useEffect(() => {
    if (!user?.uid || !isFocused) return;
    let cancelled = false;
    let unsubscribeDm: (() => void) | null = null;
    let unsubscribeGroups: (() => void) | null = null;
    setLoading(true);
    setError(null);

    if (activeAccountId) {
      const identityId = activeAccountId;
      unsubscribeDm = subscribeIdentityDmInbox(identityId, (next) => {
        if (cancelled || activeAccountIdRef.current !== identityId || next.identityId !== identityId) return;
        dmFingerprintRef.current = inboxFingerprint(next);
        setDmInbox(next);
        setLoading(false);
      }, (e) => {
        if (cancelled || activeAccountIdRef.current !== identityId) return;
        setError(getChatErrorMessage(e, "Impossible de charger les messages directs."));
        setLoading(false);
      });

      unsubscribeGroups = subscribeIdentityGroupInbox(identityId, (threads) => {
        if (cancelled || activeAccountIdRef.current !== identityId) return;
        setGroupThreads(threads);
        setLoading(false);
      }, (e) => {
        if (cancelled || activeAccountIdRef.current !== identityId) return;
        setError(getChatErrorMessage(e, "Impossible de charger les discussions de groupe."));
        setLoading(false);
      });
    } else {
      setDmInbox({ identityId: "", threads: [], peers: {} });
      setGroupThreads([]);
      setLoading(false);
    }

    return () => {
      cancelled = true;
      unsubscribeDm?.();
      unsubscribeGroups?.();
    };
  }, [activeAccountId, isFocused, user?.uid]);

  const refresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    setError(null);
    try {
      await loadDm(false);
    } catch (e) {
      setError(getChatErrorMessage(e, "Impossible d'actualiser la messagerie."));
    } finally {
      setRefreshing(false);
    }
  }, [loadDm, refreshing]);

  const rows = useMemo<InboxRow[]>(() => {
    const dmRows: InboxRow[] = visibleDmInbox.threads.map((thread) => ({
      kind: "dm",
      id: `dm:${thread.conversationId}`,
      thread,
      peer: visibleDmInbox.peers[thread.peerIdentityId],
    }));
    const groupRows: InboxRow[] = groupThreads.map((thread) => ({
      kind: "group",
      id: `group:${thread.groupId}`,
      thread,
    }));
    return [...dmRows, ...groupRows].sort((a, b) => {
      const left = a.kind === "dm"
        ? a.thread.lastMessageAt || a.thread.updatedAt
        : toMillis(a.thread.lastMessageAt || a.thread.updatedAt);
      const right = b.kind === "dm"
        ? b.thread.lastMessageAt || b.thread.updatedAt
        : toMillis(b.thread.lastMessageAt || b.thread.updatedAt);
      return right - left;
    });
  }, [groupThreads, visibleDmInbox]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      if (row.kind === "dm") return identityTitle(row.peer).toLowerCase().includes(term);
      return String(row.thread.groupName || "Groupe").toLowerCase().includes(term);
    });
  }, [rows, search]);

  const renderInboxRow = useCallback((item: InboxRow) => {
    if (item.kind === "dm") {
      const peer = item.peer;
      const unread = isIdentityDmUnread(item.thread, activeAccountId || "");
      return <Pressable key={item.id} onPress={() => router.push({ pathname: "/(tabs)/messages/direct/[id]", params: { id: item.thread.conversationId } })} style={{ backgroundColor: CARD, borderWidth: 1, borderColor: unread ? border.brand : BORDER, borderRadius: 16, padding: 12, marginBottom: 8, flexDirection: "row", gap: 12, alignItems: "center" }}>
        <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: surface.elevated, alignItems: "center", justifyContent: "center" }}><Text style={{ color: uiText.primary, fontWeight: "900" }}>{identityTitle(peer)[0]?.toUpperCase() || "?"}</Text></View>
        <View style={{ flex: 1, minWidth: 0 }}><View style={{ flexDirection: "row", justifyContent: "space-between", gap: 8 }}><Text numberOfLines={1} style={{ color: uiText.primary, fontWeight: unread ? "900" : "800", flexShrink: 1 }}>{identityTitle(peer)}</Text><Text style={{ color: MUTED, fontSize: 12 }}>{formatTime(item.thread.lastMessageAt)}</Text></View><Text style={{ color: getPrimaryIdentityBadge(peer) ? uiText.brand : MUTED, fontSize: 11, marginTop: 2 }}>{getPrimaryIdentityBadge(peer)?.label || "Identité Neqraw"}</Text><Text numberOfLines={1} style={{ color: unread ? uiText.primary : MUTED, marginTop: 5 }}>{item.thread.lastMessageText || "Commence la discussion"}</Text></View>
      </Pressable>;
    }
    const title = item.thread.groupName || "Groupe";
    const sender = item.thread.lastMessageSenderSnapshot?.displayName
      || (item.thread.lastMessageSenderSnapshot?.handle
        ? `@${item.thread.lastMessageSenderSnapshot.handle}`
        : "");
    const unread = isIdentityGroupUnread(item.thread, activeAccountId || "");
    return <Pressable
      key={item.id}
      onPress={() => router.push({ pathname: "/(tabs)/messages/group-thread/[id]", params: { id: item.thread.groupId } })}
      style={{ backgroundColor: CARD, borderWidth: 1, borderColor: unread ? border.brand : BORDER, borderRadius: 16, padding: 12, marginBottom: 8, flexDirection: "row", gap: 12, alignItems: "center" }}
    >
      <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: surface.elevated, alignItems: "center", justifyContent: "center" }}>
        <Ionicons name="people" size={20} color={ACCENT} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: "row", justifyContent: "space-between", gap: 8 }}>
          <Text numberOfLines={1} style={{ color: uiText.primary, fontWeight: unread ? "900" : "800", flexShrink: 1 }}>{title}</Text>
          <Text style={{ color: MUTED, fontSize: 12 }}>{formatTime(item.thread.lastMessageAt)}</Text>
        </View>
        <Text style={{ color: uiText.brand, fontSize: 11, marginTop: 2 }}>Groupe · {item.thread.role}</Text>
        <Text numberOfLines={1} style={{ color: unread ? uiText.primary : MUTED, marginTop: 5 }}>
          {item.thread.lastMessageText ? `${sender ? `${sender} : ` : ""}${item.thread.lastMessageText}` : "Aucun message"}
        </Text>
      </View>
    </Pressable>;
  }, [activeAccountId]);

  const emptyInbox = <View style={{ backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 16, padding: 16 }}><Text style={{ color: uiText.primary, fontWeight: "900" }}>Aucune discussion</Text><Text style={{ color: MUTED, marginTop: 6 }}>Commence une conversation avec une personne, un professeur ou une école.</Text></View>;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "left", "right"]}>
      <View style={{ alignSelf: "center", maxWidth: contentMaxWidths.feed, width: "100%", paddingHorizontal: 16, paddingTop: 8, flex: 1 }}>
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 10, minWidth: 0 }}>
            <WebBackButton fallbackHref="/(tabs)" />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ color: uiText.primary, fontSize: 28, fontWeight: "900" }}>Messages</Text>
              <Text style={{ color: MUTED, marginTop: 4 }} numberOfLines={1}>Inbox de {activeAccount?.displayName || "ton identité"}</Text>
              <Text style={{ color: MUTED, marginTop: 3, fontSize: 11 }} numberOfLines={1}>DM et groupes propres à cette identité</Text>
            </View>
          </View>
          <View style={{ flexDirection: "row", gap: 8 }}>
            {Platform.OS === "web" ? <Pressable onPress={refresh} disabled={refreshing} accessibilityRole="button" accessibilityLabel="Actualiser les discussions" style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, alignItems: "center", justifyContent: "center", opacity: refreshing ? 0.6 : 1 }}><Ionicons name="refresh" size={19} color={uiText.primary} /></Pressable> : null}
            <Pressable onPress={() => router.push("/(tabs)/messages/new")} accessibilityRole="button" accessibilityLabel="Nouvelle discussion" style={{ width: 44, height: 44, borderRadius: 14, backgroundColor: ACCENT, alignItems: "center", justifyContent: "center" }}><Ionicons name="create-outline" size={22} color={uiText.primary} /></Pressable>
          </View>
        </View>

        {accounts.length > 1 ? <View style={{ marginTop: 12 }}><AccountSwitcher compact label="Inbox active" /></View> : null}

        <View style={{ marginTop: 12, backgroundColor: CARD, borderRadius: 14, borderWidth: 1, borderColor: BORDER, paddingHorizontal: 12, height: 46, flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Ionicons name="search" size={18} color={MUTED} />
          <BrandTextInput value={search} onChangeText={setSearch} placeholder="Rechercher une discussion…" placeholderTextColor={MUTED} style={{ flex: 1, color: uiText.primary }} />
        </View>

        {error ? <Text style={{ color: uiText.danger, marginTop: 10 }}>{error}</Text> : null}

        {loading && rows.length === 0 ? <View style={{ flex: 1, alignItems: "center", justifyContent: "center", gap: 8 }}><ActivityIndicator color={ACCENT} /><Text style={{ color: MUTED }}>Chargement des messages…</Text></View> : Platform.OS === "web" ? (
          <ScrollView style={{ marginTop: 12 }} contentContainerStyle={{ paddingBottom: 24 }} keyboardShouldPersistTaps="handled">
            {filtered.length ? filtered.map(renderInboxRow) : emptyInbox}
          </ScrollView>
        ) : (
          <FlatList
            style={{ marginTop: 12 }}
            data={filtered}
            keyExtractor={(item) => item.id}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} />}
            contentContainerStyle={{ paddingBottom: 24 }}
            ListEmptyComponent={emptyInbox}
            renderItem={({ item }) => renderInboxRow(item)}
          />
        )}
      </View>
    </SafeAreaView>
  );
}
